/** CSS contract: css-library/components/02-containers/tooltip.css. */
import type { Ref } from 'react';
import { cloneElement, useId, useLayoutEffect, useRef, useState, type ButtonHTMLAttributes, type HTMLAttributes, type ReactElement, type ReactNode } from 'react';
import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip';
import { useModalPopover, usePopoverContainer } from '../lib/modalContext';

export function Tooltip({ children }: { children: ReactElement<HTMLAttributes<HTMLElement> & { disabled?: boolean; 'data-hover-disclosure'?: boolean }> }) {
  const { title, 'aria-label': ariaLabel, 'aria-labelledby': labelledBy, disabled, 'data-hover-disclosure': hoverDisclosure } = children.props;
  const tooltipId = useId();
  const control = useRef<HTMLElement>(null);
  const container = usePopoverContainer();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState(title || ariaLabel || '');
  const [above, setAbove] = useState(false);
  // Referenced DOM labels can change independently; the equality guard bounds updates.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const node = control.current;
    if (!node) return;
    const referenced = labelledBy?.split(/\s+/).map((id) => node.ownerDocument.getElementById(id)?.textContent ?? '').join(' ');
    const input = node.parentElement?.querySelector('input');
    const inputLabel = input?.getAttribute('aria-label') || Array.from(input?.labels ?? []).map((label) => label.textContent?.trim()).join(' ');
    const visible = node.textContent?.trim() || node.closest('.card')?.querySelector('.card-action-label')?.textContent?.trim() || '';
    const normalize = (text: string) => text.replace(/\s+/g, ' ').trim();
    const next = visible
      ? (title && normalize(title) !== normalize(visible) ? title : '')
      : title || referenced || ariaLabel || inputLabel || '';
    if (label !== next) setLabel(next);
    setAbove(Boolean(node.closest('.pill--tip-above')));
  });
  useModalPopover(open, () => setOpen(false));
  return <BaseTooltip.Root open={open} onOpenChange={(next, details) => {
    if (!next && details.reason === 'escape-key') {
      details.event.preventDefault();
      details.event.stopPropagation();
      details.event.stopImmediatePropagation();
    }
    if (next && control.current?.querySelector('[data-control-tooltip]:hover, [data-control-tooltip]:focus-visible')) return;
    setOpen(next);
  }} disabled={!label || disabled || hoverDisclosure}>
    <BaseTooltip.Trigger ref={(node: HTMLElement | null) => { control.current = node; }} delay={300} render={cloneElement(children, { title: '',
      ...{ 'data-control-tooltip': '' },
      onPointerOver: (event) => {
        children.props.onPointerOver?.(event);
        if ((event.target as Element).closest('[data-control-tooltip]') !== event.currentTarget) setOpen(false);
      },
      'aria-describedby': [children.props['aria-describedby'], open ? tooltipId : undefined].filter(Boolean).join(' ') || undefined })} />
    <BaseTooltip.Portal container={container}>
      <BaseTooltip.Positioner className="tooltip-positioner" side={above ? 'top' : 'bottom'} sideOffset={10} collisionPadding={8}>
        <BaseTooltip.Popup role="tooltip" id={tooltipId} className="tooltip">{label}</BaseTooltip.Popup>
      </BaseTooltip.Positioner>
    </BaseTooltip.Portal>
  </BaseTooltip.Root>;
}

export function ButtonControl(
  { ref, type = 'button', ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { ref?: Ref<HTMLButtonElement> },
) {
  return <Tooltip><button ref={ref} type={type} {...props} /></Tooltip>;
}

export function TooltipProvider({ children }: { children: ReactNode }) {
  return <BaseTooltip.Provider delay={300}>{children}</BaseTooltip.Provider>;
}
